/**
 * Application entry.
 *
 * The dev fixture layer is loaded through a dynamic import guarded by
 * `import.meta.env.DEV`. That constant folds to `false` in a production build,
 * so Rollup drops the branch and the fixture module never enters the bundle —
 * the mock server is not merely disabled in production, it is absent.
 */

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClientProvider } from '@tanstack/react-query';
import { App } from './App';
import { AuthProvider } from './lib/auth';
import { readConfig } from './lib/config';
import { createQueryClient } from './lib/queryClient';
import { I18nProvider, tr } from './i18n';
import { ThemeProvider } from './lib/theme';
import './styles/global.css';

async function start() {
  const container = document.getElementById('root');
  if (!container) throw new Error('Missing #root element.');

  let config;
  try {
    config = readConfig();
  } catch (error) {
    container.innerHTML = '';
    const message = document.createElement('pre');
    // var(--danger), not a literal: this screen renders before React, on top of
    // whichever theme the pre-paint script settled, and a baked-in dark-mode red
    // would be the least readable thing on the page in light mode -- on the one
    // screen that has to survive everything else having failed.
    message.style.cssText =
      'padding:24px;color:var(--danger);white-space:pre-wrap;font-family:monospace';
    message.textContent =
      error instanceof Error ? error.message : tr().config.notConfigured;
    container.appendChild(message);
    return;
  }

  if (import.meta.env.DEV && config.useDevFixtures) {
    const { installFixtureServer } = await import('./dev/fixtureServer');
    installFixtureServer(config.apiBaseUrl);
  }

  createRoot(container).render(
    <StrictMode>
      {/*
        I18nProvider is outermost: the auth gate, the query error boundaries and
        the config failure screen all produce operator-facing text, and every
        one of them renders before any feature component does.
      */}
      <I18nProvider>
        <ThemeProvider>
          <QueryClientProvider client={createQueryClient()}>
            <AuthProvider config={config}>
              <App />
            </AuthProvider>
          </QueryClientProvider>
        </ThemeProvider>
      </I18nProvider>
    </StrictMode>,
  );
}

void start();
